import { ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { nanoid } from 'nanoid'
import type {
  FurnitureItem,
  LocalFurnitureItem,
  SubRegionItem,
  LocalSubRegionItem,
  EchoSubRegions,
} from '@/api/room/types'
import {
  convert_region_r2c,
  convert_region_c2r,
  rotateRect_cw,
  rotateRect_ccw,
  convert_furniture_r2c,
  convert_furniture_c2r,
} from '@/utils/coordTransform'

export const useRoomStore = defineStore(
  'room',
  () => {
    /* ========== 接口元数据 ========== */
    const radarAngle = ref(0) // 雷达旋转角度
    const furnitureItems = ref<FurnitureItem[]>([]) // 家具列表
    const subRegionItems = ref<SubRegionItem[]>([]) // 子区域列表

    /* ========== 本地元数据 ========== */
    const localFurnitureItems = ref<LocalFurnitureItem[]>([]) // 家具列表
    const localSubRegions = ref<LocalSubRegionItem[]>([]) // 子区域列表

    /* ========== 缓存元数据 ========== */
    /**
     * 缓存接口中的家具列表
     * @param items - 家具列表
     */
    function cacheFurniture(items: FurnitureItem & LocalFurnitureItem[]) {
      // 过滤掉雷达家具
      const furniture = items.filter((item) => item.type !== 'radar')
      furnitureItems.value = furniture
      localFurnitureItems.value = furniture.map((item) => ({
        ...item,
        ...handlerFurnitureR2C(item),
        nanoid: nanoid(),
        width: item.width,
        length: item.length,
      }))
    }

    /**
     * 缓存接口中的子区域列表
     * @param items - 子区域列表
     */
    function cacheSubRegion(items: EchoSubRegions[]) {
      subRegionItems.value = items.map((item) => ({
        startXx: item.x_cm_start ?? 0,
        stopXx: item.x_cm_stop ?? 0,
        startYy: item.y_cm_start ?? 0,
        stopYy: item.y_cm_stop ?? 0,
        startZz: item.z_cm_start ?? 0,
        stopZz: item.z_cm_stop ?? 0,
        isLowSnr: item.is_low_snr,
        isDoor: item.is_door,
        presenceEnterDuration: item.presence_enter_duration,
        presenceExitDuration: item.presence_exit_duration,
        trackPresence: item.track_presence,
        excludeFalling: item.exclude_falling,
      }))
      const localSub = ref<LocalSubRegionItem[]>([])
      localSub.value = subRegionItems.value.map((item) => {
        return {
          ...item,
          nanoid: nanoid(),
          isDraging: false,
          isResizing: false,
          isActive: false,
          isTracking: item.trackPresence === 1,
          isFalling: item.excludeFalling === 1,
          isBed: item.isLowSnr === 1,
        }
      })
      localSubRegions.value = localSub.value.map((item) => {
        return {
          ...item,
          ...handlerRegionR2C(item),
        }
      })
    }

    // 设置雷达旋转角度
    const setRadarAngle = (angle: number) => {
      radarAngle.value = angle
      // 角度变化时重新计算所有家具的位置
      localFurnitureItems.value = localFurnitureItems.value.map((item) => ({
        ...item,
        ...handlerFurnitureR2C(item, angle),
      }))
      // 同时更新子区域位置
      localSubRegions.value = localSubRegions.value.map((item) => ({
        ...item,
        ...handlerRegionR2C(item, angle),
      }))
    }

    // 创建子区域
    const createSubRegion = () => {
      if (localSubRegions.value.length < 6) {
        const localNanoid = nanoid()
        const defaultRegion = {
          nanoid: localNanoid,
          startXx: 0,
          stopXx: 0,
          startYy: 0,
          stopYy: 0,
          startZz: 0,
          stopZz: 0,
          pixelX: 250,
          pixelY: 250,
          pixelWidth: 0,
          pixelHeight: 0,
          isDraging: false,
          isResizing: false,
          isActive: false,
          isTracking: false,
          isFalling: false,
          isBed: false,
          isLowSnr: 0,
          isDoor: 0,
          presenceEnterDuration: 3,
          presenceExitDuration: 3,
          trackPresence: 0,
          excludeFalling: 0,
        }

        localSubRegions.value.push(defaultRegion)
        handlerRegionR2C(defaultRegion)

        // 如果添加的是床，就将子区域设置为呼吸检测区域
        if (localFurnitureItems.value.some((item) => item.type === 'bed')) {
          localSubRegions.value[0].isBed = true
          localSubRegions.value[0].isLowSnr = 1
        }
      }
    }

    /**
     * 监听添加家具调整首个子区域
     * 解决添加了床，但是没有设置为呼吸检测的问题
     */
    watch(
      () => localFurnitureItems.value,
      (newVal) => {
        if (newVal.some((item) => item.type === 'bed' && localSubRegions.value.length > 0)) {
          localSubRegions.value[0].isBed = true
          localSubRegions.value[0].isLowSnr = 1
        }
      },
      {
        deep: true,
      }
    )

    /* ========== 坐标转换 ========== */

    // 家具列表坐标转换    雷达坐标系 --> 画布坐标系
    const handlerFurnitureR2C = (item: LocalFurnitureItem, angle: number = radarAngle.value) => {
      // ⚠️ 家具长度和宽度需要交换一下，不然会存在位置偏移
      // const itemWidth = angle === 0 || angle === 180 ? item.length : item.width
      // const itemLength = angle === 0 || angle === 180 ? item.width : item.length
      const furnitureItem = convert_furniture_r2c(
        {
          x: item.x,
          y: item.y,
          width: item.width,
          height: item.length,
        },
        {
          x_radar: 250,
          y_radar: 250,
        }
      )
      const furnitureRotate = rotateRect_cw(
        {
          left: furnitureItem.left,
          top: furnitureItem.top,
          width: furnitureItem.width,
          height: furnitureItem.height,
        },
        {
          x: 250,
          y: 250,
        },
        angle
      )

      const target = localFurnitureItems.value.find((sub) => sub.nanoid === item.nanoid)
      if (target) {
        target.left = furnitureRotate.left
        target.top = furnitureRotate.top
        target.width = furnitureRotate.width
        target.length = furnitureRotate.height
      }

      return {
        ...item,
        left: furnitureRotate.left,
        top: furnitureRotate.top,
        width: furnitureRotate.width,
        length: furnitureRotate.height,
      }
    }

    // 家具列表坐标转换    画布坐标系 --> 雷达坐标系
    const handlerFurnitureC2R = (item: LocalFurnitureItem, angle: number = radarAngle.value) => {
      const furnitureRotate = rotateRect_ccw(
        {
          left: item.left,
          top: item.top,
          width: item.width,
          height: item.length,
        },
        {
          x: 250,
          y: 250,
        },
        angle
      )

      const furnitureItem = convert_furniture_c2r(
        {
          left: furnitureRotate.left,
          top: furnitureRotate.top,
          width: furnitureRotate.width,
          height: furnitureRotate.height,
        },
        {
          x_radar: 250,
          y_radar: 250,
        }
      )

      const target = localFurnitureItems.value.find((sub) => sub.nanoid === item.nanoid)
      if (target) {
        target.x = furnitureItem.x
        target.y = furnitureItem.y
      }

      return {
        ...item,
        x: furnitureItem.x,
        y: furnitureItem.y,
      }
    }

    // 子区域列表坐标转换    雷达坐标系 --> 画布坐标系
    const handlerRegionR2C = (item: LocalSubRegionItem, angle: number = radarAngle.value) => {
      const regionItem = convert_region_r2c(
        {
          x_cm_start: Number(item.startXx),
          x_cm_stop: Number(item.stopXx),
          y_cm_start: Number(item.startYy),
          y_cm_stop: Number(item.stopYy),
        },
        {
          x_radar: 250,
          y_radar: 250,
        }
      )

      const routeRegion = rotateRect_cw(regionItem, { x: 250, y: 250 }, angle)

      const target = localSubRegions.value.find((sub) => sub.nanoid === item.nanoid)
      if (target) {
        target.startXx = isNaN(Number(target.startXx)) ? 0 : Number(target.startXx)
        target.stopXx = isNaN(Number(target.stopXx)) ? 0 : Number(target.stopXx)
        target.startYy = isNaN(Number(target.startYy)) ? 0 : Number(target.startYy)
        target.stopYy = isNaN(Number(target.stopYy)) ? 0 : Number(target.stopYy)
        target.startZz = isNaN(Number(target.startZz)) ? 0 : Number(target.startZz)
        target.stopZz = isNaN(Number(target.stopZz)) ? 0 : Number(target.stopZz)
        target.pixelX = routeRegion.left
        target.pixelY = routeRegion.top
        target.pixelWidth = routeRegion.width
        target.pixelHeight = routeRegion.height
      }

      return {
        ...item,
        pixelX: routeRegion.left,
        pixelY: routeRegion.top,
        pixelWidth: routeRegion.width,
        pixelHeight: routeRegion.height,
      }
    }

    // 子区域列表坐标转换    画布坐标系 --> 雷达坐标系
    const handlerRegionC2R = (item: LocalSubRegionItem, angle: number = radarAngle.value) => {
      const routeRegion = rotateRect_ccw(
        {
          left: item.pixelX,
          top: item.pixelY,
          width: item.pixelWidth,
          height: item.pixelHeight,
        },
        { x: 250, y: 250 },
        angle
      )

      const regionItem = convert_region_c2r(
        {
          left: routeRegion.left,
          top: routeRegion.top,
          width: routeRegion.width,
          height: routeRegion.height,
        },
        {
          x_radar: 250,
          y_radar: 250,
        }
      )

      const target = localSubRegions.value.find((sub) => sub.nanoid === item.nanoid)
      if (target) {
        target.startXx = Math.round(regionItem.x_cm_start)
        target.stopXx = Math.round(regionItem.x_cm_stop)
        target.startYy = Math.round(regionItem.y_cm_start)
        target.stopYy = Math.round(regionItem.y_cm_stop)
      }

      return {
        ...item,
        startXx: Math.round(regionItem.x_cm_start),
        stopXx: Math.round(regionItem.x_cm_stop),
        startYy: Math.round(regionItem.y_cm_start),
        stopYy: Math.round(regionItem.y_cm_stop),
      }
    }

    return {
      radarAngle,
      furnitureItems,
      subRegionItems,
      localFurnitureItems,
      localSubRegions,
      cacheFurniture,
      cacheSubRegion,
      handlerRegionR2C,
      handlerRegionC2R,
      setRadarAngle,
      createSubRegion,
      handlerFurnitureR2C,
      handlerFurnitureC2R,
    }
  },
  {
    persist: {
      key: `${import.meta.env.VITE_STORE_NAME}_ROOM_INFO`,
      storage: localStorage,
    },
  }
)
